import type { VaultsSliceGetter, VaultsSliceSetter, } from "@store/vaults";
import { produce, } from "immer";
import type { Store, } from "@store";


/**
 * Applies calculated exposure data to the vaults state by aggregating exposure data from all available vaults
 * and updating the global exposure state with computed values including PnL, cost basis, and gains.
 * 
 * @param {VaultsSliceSetter} set - Function to update the vaults state
 * @param {VaultsSliceGetter} get - Function to get current vaults state
 * @returns {Promise<void>} Promise that resolves when exposure calculations are complete
 */
export const applyCalculatedExposure = async ( set: VaultsSliceSetter, get: VaultsSliceGetter, ): Promise<void> => {

	const { available, } = get().vaults;

	const open_value = available.reduce((acc: number, { exposure, }) => acc + exposure.current_value.usd, 0);
	const open_cost_basis = available.reduce((acc: number, { exposure, }) => acc + exposure.cost_basis.usd, 0);
	const orig_cost_basis = available.reduce((acc: number, { exposure, }) => acc + exposure.orig_cost_basis.usd, 0);
	const realized_gain = available.reduce((acc: number, { exposure, }) => acc + exposure.realized_gain.usd, 0);
	const unrealized_gain = available.reduce((acc: number, { exposure, }) => acc + exposure.unrealized_gain.usd, 0);
	const unrealized_gain_perc = unrealized_gain > 0 && open_cost_basis > 0 ? (unrealized_gain / open_cost_basis) * 100 : 0;
	const est_annual_earnings = available.reduce((acc: number, { exposure, }) => acc + exposure.est_annual_earnings.usd, 0);
	const pnl = available.reduce((acc: number, { exposure, }) => acc + exposure.unrealized_gain.usd + exposure.realized_gain.usd, 0);
	const pnl_perc = ( (realized_gain + unrealized_gain) / orig_cost_basis * 100 ) || 0;

	set(produce<Store>(state => {
		state.vaults.exposure = state.vaults.exposure || {};
		state.vaults.exposure.open_value = { usd: open_value, };
		state.vaults.exposure.open_cost_basis = { usd: open_cost_basis };
		state.vaults.exposure.orig_cost_basis = { usd: orig_cost_basis };
		state.vaults.exposure.realized_gain = { usd: realized_gain, };
		state.vaults.exposure.unrealized_gain = { usd: unrealized_gain, perc: unrealized_gain_perc, };
		state.vaults.exposure.pnl = { usd: pnl, perc: pnl_perc, };
		state.vaults.exposure.est_annual_earnings = { usd: est_annual_earnings, };
	}));

};

export const applyNoExposure = async ( set: VaultsSliceSetter, get: VaultsSliceGetter, ): Promise<void> => {

	set(produce<Store>(state => {
		state.vaults.exposure = {
			open_value: { usd: 0, },
			open_cost_basis: { usd: 0, },
			orig_cost_basis: { usd: 0, },
			realized_gain: { usd: 0, },
			unrealized_gain: { usd: 0, perc: 0, },
			pnl: { usd: 0, perc: 0, },
			est_annual_earnings: { usd: 0, },
		};
	}));

};
